import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import { Divider, IconButton, Stack, SvgIcon, Typography } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { noop } from "lodash";
import { FC } from "react";
import toHHMMSS from "../../../util/toHHMMSS";
import IwAudioLoader from "../../app/IwAudioLoader";
import MaterialSpeakerPopup from "./material-speaker-popup";
import DeleteOutlinedIcon from "../../../../icons/DeleteOutlinedIcon";
import PlayAudioIcon from "../../../../icons/PlayAudioIcon";
import PauseAudioIcon from "../../../../icons/PauseAudioIcon";

interface TranscriptSpeakerElementProps {
    documentID: string;
    speaker: { name: string; id: string };
    time: string;
    speakerDictionary: { name: string; id: string }[];
    speakerLibrary: any[];
    isReadOnly: boolean;
    children?: any;
    handlePublishSpeakerChange?: (documentID, newSpeaker) => void;
    handleRemoveSpeaker?: (documentID) => void;
    onPlayPaus?: () => void;
}

const TranscriptSpeakerElement: FC<TranscriptSpeakerElementProps> = (props) => {
    const {
        time,
        speakerDictionary,
        speakerLibrary,
        speaker,
        isReadOnly,
        children,
        handlePublishSpeakerChange,
        handleRemoveSpeaker,
        onPlayPaus,
        documentID,
    } = props;
    const formattedTime = toHHMMSS(time);
    const theme = useTheme();
    return (
        <Stack
            id={`speaker-document-${documentID}`}
            className="transcript-editor-speaker-element"
            direction="row"
            sx={{
                margin: "0 0 8px",
                alignItems: "center",
            }}
        >
            <Stack
                className={`transcript-editor-speaker-element__container ${
                    !isReadOnly
                        ? "transcript-editor-speaker-element__container--editing"
                        : ""
                }`}
                direction="row"
                sx={{
                    display: "inline-flex",
                    boxShadow: isReadOnly ? "none" : `0 0 0 1px ${theme.custom.borderGrey}`,
                    borderRadius: "4px",
                    alignItems: "center",
                }}
            >
                {isReadOnly ? (
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "16px",
                            lineHeight: 1,
                        }}
                    >
                        {speaker?.name}
                    </Typography>
                ) : (
                    <Stack
                        className="transcript-editor-speaker-element__popup"
                        direction="row"
                        sx={{
                            alignItems: "center",
                            ".MuiOutlinedInput-input": {
                                paddingTop: "0 !important",
                                paddingBottom: "0 !important",
                                fontWeight: "700",
                                ["WebkitTextFillColor"]: `${theme.custom.text} !important`,
                            },
                        }}
                    >
                        <MaterialSpeakerPopup
                            onChange={(newSpeaker) => {
                                handlePublishSpeakerChange(documentID, newSpeaker);
                            }}
                            speakerDictionary={speakerDictionary}
                            speakerLibrary={speakerLibrary}
                            value={speaker?.id}
                            disabled={isReadOnly}
                        />
                    </Stack>
                )}
                {isReadOnly ? (
                    <Typography
                        className="transcript-editor-speaker-element__time"
                        sx={{
                            fontSize: "14px",
                            color: theme.custom.mainIconColor,
                            lineHeight: 1,
                            marginLeft: "16px !important",
                        }}
                    >
                        {formattedTime}
                    </Typography>
                ) : (
                    <>
                        <Divider
                            flexItem={true}
                            orientation="vertical"
                        />
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px 12px",
                                fontSize: "14px",
                                fontWeight: "600",
                            }}
                        >
                            {formattedTime}
                        </Stack>
                    </>
                )}
                {!isReadOnly && (
                    <>
                        <Divider
                            flexItem={true}
                            orientation="vertical"
                        />
                        <IconButton
                        className="transcript-editor-speaker-element__delete-button"
                            sx={{
                                borderRadius: "4px",
                            }}
                            disabled={isReadOnly}
                            onClick={() => {
                                handleRemoveSpeaker(documentID);
                            }}
                        >
                            <DeleteOutlinedIcon/>
                        </IconButton>
                        <Divider
                            flexItem={true}
                            orientation="vertical"
                        />
                    </>
                )}
                <IconButton
                    className="transcript-editor-speaker-element__play-button"
                    sx={{
                        borderRadius: isReadOnly ? "100px" : "4px",
                        marginLeft: isReadOnly ? "6px" : 0,
                        padding: isReadOnly ? 0 : "7.5px",
                        marginBottom: isReadOnly ? "2px" : 0,
                        ".MuiSvgIcon-root": {
                            fontSize: isReadOnly ? "24px" : "20px",
                        },
                    }}
                    onClick={() => {
                        onPlayPaus();
                    }}
                >
                    <SvgIcon className="play-icon">
                        <PlayAudioIcon/> 
                    </SvgIcon>
                    <SvgIcon className="pause-icon">
                        <PauseAudioIcon/> 
                    </SvgIcon>
                </IconButton>
            </Stack>
            {children}
            <Stack
                className="transcript-editor-speaker-element__loader"
                sx={{
                    alignItems: "center",
                    marginLeft: "2px",
                }}
            >
                <IwAudioLoader />
            </Stack>
        </Stack>
    );
};

TranscriptSpeakerElement.defaultProps = {
    children: null,
    handlePublishSpeakerChange: noop,
    handleRemoveSpeaker: noop,
    onPlayPaus: noop,
};

export default TranscriptSpeakerElement;
